// ============================================================
// MASH evidence artifacts — DISPLAY titles (ME-CARDS-R1 A1)
// ============================================================
// The bot-emitted `artifact.title` is the artifact's IDENTITY: `midasPageSlice`
// dedupes rich artifacts on `type + title`, so three same-turn cards stay three
// tabs and re-opening one stays a no-op. That stored title must NEVER be
// rewritten.
//
// But the live 22.08 payload put the machine GUID in the title, so all three
// dock tabs, the under-reply affordance chips and the fullscreen breadcrumb read
// `d72d27d9-0308-49e…` — identical at a glance and unusable.
//
// This module is the RENDER-TIME display layer: it derives a human label from
// the card's own contents (identity + section), leaving the stored title, the
// dedupe keys and every `openArtifact` payload untouched.
import type {
  MachineCardArtifactData,
  MachineEvidenceArtifactData,
} from "../../../../../shared/components/machineEvidence";
import {
  looksLikeGuid,
  resolveIdentityTitle,
} from "../../../../../shared/components/machineEvidence";
import { SIX_TAB_LABELS, isOverviewCard } from "./machineCardConstants";

/** Structural minimum shared by `midasPageModels.Artifact` (dock tabs) and
 *  `chatBotModels.ChatArtifact` (the under-reply affordance chips). */
export interface TitleBearingArtifact {
  type: string;
  title?: string | null;
  data?: unknown;
}

/** Minimal shape of the i18next `t` we need. Returns `unknown` because i18next's
 *  own return type is not plainly `string` — call sites already cast. */
export type MachineTitleTranslate = (key: string, options?: Record<string, unknown>) => unknown;

const text = (value: unknown): string => (typeof value === "string" ? value : String(value ?? ""));

/** The translated identity fallback, used whenever no GUID-free name survives. */
export const machineTitleFallback = (translate: MachineTitleTranslate): string =>
  text(translate("card.titleFallback", { defaultValue: "Machine evidence" }));

/**
 * The label to RENDER for an artifact tab / chip / breadcrumb.
 *
 *  - `machine_card`     -> `${identity} — ${section}` (e.g. "MASHi2601 — Arm"),
 *                          identity via the GUID-hardened `resolveIdentityTitle`,
 *                          section = "Machine overview" for a roll-up card, else
 *                          the populated tab's translated label.
 *  - `machine_evidence` -> the bot title when it carries no GUID-shaped token,
 *                          else `${subsystem label} · ${fallback}`.
 *  - anything else      -> the bot title, unchanged.
 */
export const machineArtifactDisplayTitle = (
  artifact: TitleBearingArtifact | null | undefined,
  translate: MachineTitleTranslate
): string => {
  if (!artifact) return "";
  const rawTitle = artifact.title || "";

  if (artifact.type === "machine_card") {
    const data = artifact.data as unknown as MachineCardArtifactData | undefined;
    if (!data || !Array.isArray(data.tabs) || data.tabs.length === 0) {
      // Unparseable card: keep the bot title unless it leaks a GUID.
      return looksLikeGuid(rawTitle) || !rawTitle ? machineTitleFallback(translate) : rawTitle;
    }
    const identity = resolveIdentityTitle(data) || machineTitleFallback(translate);
    if (isOverviewCard(data)) {
      return `${identity} — ${text(
        translate("tab.overview", { defaultValue: "Machine overview" })
      )}`;
    }
    // Per-part card: exactly one section. Prefer a POPULATED tab so a card that
    // also carries placeholder sections is still named after its real content.
    const tab = data.tabs.find((t) => (t.figures?.length ?? 0) > 0) ?? data.tabs[0];
    const section = text(
      translate(`tab.${tab.part_slug}`, {
        defaultValue: SIX_TAB_LABELS[tab.part_slug] || tab.name || tab.part_slug,
      })
    );
    return section ? `${identity} — ${section}` : identity;
  }

  if (artifact.type === "machine_evidence") {
    if (rawTitle && !looksLikeGuid(rawTitle)) return rawTitle;
    const data = artifact.data as unknown as MachineEvidenceArtifactData | undefined;
    const subsystem = data?.subsystem;
    const fallback = machineTitleFallback(translate);
    if (!subsystem) return fallback;
    const label = text(
      translate(`tab.${subsystem}`, { defaultValue: SIX_TAB_LABELS[subsystem] || subsystem })
    );
    return label ? `${label} · ${fallback}` : fallback;
  }

  return rawTitle;
};

export default machineArtifactDisplayTitle;
